import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import { useToast } from '../hooks/use-toast';

const GroupContext = createContext(null);

export const useGroupContext = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroupContext must be used within a GroupContextProvider');
  }
  return context;
};

export const GroupContextProvider = ({ children }) => {
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [orchestratorsForGroups, setOrchestratorsForGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const { toast } = useToast();

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  // Load groups when user is available
  useEffect(() => {
    if (user) {
      loadGroups();
    }
  }, [user]);

  const loadGroups = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await api.get('/groups');
      setGroups(response.data);
    } catch (err) {
      console.error('Failed to load groups:', err);
      toast({
        title: 'Error',
        description: 'Failed to load groups',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  const myRoleFor = useCallback((group) => {
    if (!group) return null;
    // Platform admin has 'admin' role for all groups
    if (user?.role === 'admin') return 'admin';
    // Otherwise check the group's my_role
    return group.my_role || null;
  }, [user]);

  const orchestratorsForGroup = useCallback(async (groupId) => {
    if (orchestratorsForGroups[groupId]) {
      return orchestratorsForGroups[groupId];
    }
    try {
      const response = await api.get(`/groups/${groupId}/orchestrators`);
      const orchestrators = response.data;
      setOrchestratorsForGroups(prev => ({ ...prev, [groupId]: orchestrators }));
      return orchestrators;
    } catch (err) {
      console.error(`Failed to load orchestrators for group ${groupId}:`, err);
      return [];
    }
  }, [orchestratorsForGroups]);

  const value = {
    groups,
    currentGroup,
    setCurrentGroup,
    myRoleFor,
    orchestratorsForGroup,
    isPlatformAdmin: user?.role === 'admin',
    user,
    loading,
    loadGroups,
  };

  return (
    <GroupContext.Provider value={value}>
      {children}
    </GroupContext.Provider>
  );
};

export default GroupContext;
